import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bell } from "lucide-react";
import { oneSignalService } from "@/lib/onesignal";
import { supabase } from "@/integrations/supabase/client";
import { waitForNotificationsSDK, detectNativeApp } from "@/lib/platform";
import { useNotificationPromptStore } from "@/stores/notificationPromptStore";

interface NotificationPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * A dialog that explains why notifications are needed,
 * then requests native notification permissions.
 * This provides better UX than requesting permissions immediately.
 */
export const NotificationPromptDialog = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
}: NotificationPromptDialogProps) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const { markPromptShown } = useNotificationPromptStore();

  useEffect(() => {
    if (!open) {
      setIsProcessing(false);
      return;
    }

    // Mark that we've shown the prompt
    markPromptShown();

    // Check if we already have permission before showing the dialog
    const checkAndRequestPermission = async () => {
      try {
        const detection = await detectNativeApp();
        if (!detection.isNative) {
          return;
        }

        const sdkLoaded = await waitForNotificationsSDK(5000);
        if (!sdkLoaded) {
          return;
        }

        const initialized = await oneSignalService.initialize();
        if (!initialized) {
          return;
        }

        // Check if we already have permission
        const hasPermission = await oneSignalService.getPermissionStatus();
        if (hasPermission) {
          console.log('[NotificationPrompt] Permission already granted, skipping prompt');
          onOpenChange(false);
          if (onSuccess) {
            onSuccess();
          }
          return;
        }

        // Wait 2 seconds before requesting permission to give user time to read
        setTimeout(() => {
          requestNotificationPermission();
        }, 2000);
      } catch (error) {
        console.error('[NotificationPrompt] Error checking permission:', error);
      }
    };

    checkAndRequestPermission();
  }, [open]);

  const requestNotificationPermission = async () => {
    setIsProcessing(true);

    try {
      // Check if we're in native app
      const detection = await detectNativeApp();
      if (!detection.isNative) {
        throw new Error("Notifications are only available in the native iOS app.");
      }

      // Wait for SDK to load
      const sdkLoaded = await waitForNotificationsSDK(5000);
      if (!sdkLoaded) {
        throw new Error("Notification SDK not loaded. Please ensure you're using the native iOS app.");
      }

      // Initialize OneSignal
      console.log('[NotificationPrompt] Initializing OneSignal service...');
      const initialized = await oneSignalService.initialize();
      if (!initialized) {
        throw new Error("Failed to initialize notification service.");
      }

      // Request permission
      console.log('[NotificationPrompt] Requesting notification permission...');
      const permission = await oneSignalService.requestPermission(false);
      
      if (!permission) {
        throw new Error("Notification permission denied. You can enable it later in Settings.");
      }

      // Get player ID
      console.log('[NotificationPrompt] Getting OneSignal player ID...');
      const playerId = await oneSignalService.getOneSignalId();
      if (!playerId) {
        throw new Error("Failed to register device for notifications.");
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Not authenticated.");
      }

      // Set external ID
      console.log('[NotificationPrompt] Linking OneSignal player to user account...');
      const externalIdSuccess = await oneSignalService.setExternalId(user.id);
      if (!externalIdSuccess) {
        throw new Error("Failed to link notifications to your account.");
      }

      // Detect timezone
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Save to database (don't enable goal reminders by default, just register the device)
      console.log('[NotificationPrompt] Saving notification settings...');
      const { error: updateError } = await supabase
        .from("profiles_internal")
        .update({ 
          onesignal_player_id: playerId,
          user_timezone: detectedTimezone,
          // Don't enable goal_reminder_notifications_enabled by default
          // User can enable in settings if they want goal reminders
        })
        .eq("id", user.id);

      if (updateError) {
        throw new Error(`Failed to save settings: ${updateError.message}`);
      }

      console.log('[NotificationPrompt] Setup complete!');
      
      // Don't auto-close the dialog - let user dismiss it manually
      // onOpenChange(false);
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("[NotificationPrompt] Error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to enable notifications.";
      
      // Don't auto-close the dialog - let user dismiss it manually
      // onOpenChange(false);
      
      // Call error callback
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-full bg-primary/10">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-lg">{t('ui.text.stay_connected')}</DialogTitle>
          </div>
          <DialogDescription className="text-base pt-2">
            {isProcessing ? (
              <div className="text-center py-2">
                <div className="text-sm text-muted-foreground">{t('ui.text.setting_up_notifications')}</div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <Bell className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p>To get notified when your friends request to follow you or post something new, 
                  GitFit.AI needs notifications enabled.</p>
                  <div className="mt-3 text-center text-sm text-muted-foreground">{t('ui.text.you_ll_see_a_permission_request_in_a_moment')}</div>
                </div>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
